/**
 * Shared destination autocomplete for the unified "Where" inputs.
 *
 * There is no single backend endpoint that returns cities, regions/states and countries
 * together, so this fans out to all three and merges client-side:
 *   - cities    → searchCity()      (/api/v2/location/autocomplete/google?filterMode=cities)
 *   - regions   → searchDestination({ type: 'locality' })  — administrative_area_level_1
 *                 and colloquial_area, i.e. states, provinces and informal regions
 *   - countries → searchDestination({ type: 'country' })
 *
 * Cities deliberately keep using searchCity: it is the only source returning the rich
 * CitySearchResult (_id, photos, city_identifying_name) that downstream payload builders
 * and the hero photo depend on.
 *
 * Pass `citiesOnly` where the field must hold a real city (e.g. "Where do you live?"):
 * regions and countries are then never queried.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { searchCity } from '@/api/auth';
import { searchDestination } from '@/api/destinations';
import type { CitySearchResult, DestinationAutocompleteResult } from '@/types/city';
import {
  DESTINATION_MIN_QUERY_LENGTH,
  DESTINATION_SEARCH_DEBOUNCE_MS,
} from '@/constants/destinationSearch';
import {
  mergeAndRankDestinationResults,
  type AnyDestinationResult,
} from '@/utils/destinationSearchRanking';
import { debug } from '@/utils/logger';

export interface UseDestinationSearchOptions {
  /** Only query cities; skip regions/states and countries. */
  citiesOnly?: boolean;
}

export interface UseDestinationSearchResult {
  /** Merged, relevance-ranked cities + regions/states + countries. */
  results: AnyDestinationResult[];
  isLoading: boolean;
  /** Debounced search. Queries shorter than the minimum clear the results. */
  search: (query: string) => void;
  /** Drop pending requests and empty the result list (e.g. after a selection). */
  clear: () => void;
}

/** The autocomplete endpoint can return holes for predictions without a place id. */
function toCityResults(response: unknown): CitySearchResult[] {
  if (!Array.isArray(response)) return [];
  return (response as Array<CitySearchResult | null | undefined>).filter(
    (city): city is CitySearchResult => Boolean(city && city._id),
  );
}

export function useDestinationSearch({
  citiesOnly = false,
}: UseDestinationSearchOptions = {}): UseDestinationSearchResult {
  const [results, setResults] = useState<AnyDestinationResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against a slow response from an earlier query overwriting a newer one.
  // Three concurrent requests per keystroke make interleaving likely.
  const requestIdRef = useRef(0);

  const cancelPending = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    requestIdRef.current += 1;
  }, []);

  const runSearch = useCallback(async (query: string) => {
    const requestId = (requestIdRef.current += 1);
    setIsLoading(true);

    try {
      const [cityResponse, regions, countries] = await Promise.all([
        searchCity(query),
        citiesOnly ? [] : searchDestination({ type: 'locality', q: query }),
        citiesOnly ? [] : searchDestination({ type: 'country', q: query }),
      ]);

      if (requestId !== requestIdRef.current) return;

      const cities = toCityResults(cityResponse);
      const merged = mergeAndRankDestinationResults(
        query,
        cities,
        regions as DestinationAutocompleteResult[],
        countries as DestinationAutocompleteResult[],
      );

      debug('[useDestinationSearch] Search results', {
        query,
        cities: cities.length,
        regions: regions.length,
        countries: countries.length,
        merged: merged.length,
      });

      setResults(merged);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [citiesOnly]);

  const search = useCallback(
    (query: string) => {
      cancelPending();

      if (query.trim().length < DESTINATION_MIN_QUERY_LENGTH) {
        setResults([]);
        setIsLoading(false);
        return;
      }

      debounceRef.current = setTimeout(() => {
        void runSearch(query);
      }, DESTINATION_SEARCH_DEBOUNCE_MS);
    },
    [cancelPending, runSearch],
  );

  const clear = useCallback(() => {
    cancelPending();
    setResults([]);
    setIsLoading(false);
  }, [cancelPending]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return { results, isLoading, search, clear };
}

export default useDestinationSearch;
